import { useTranslation } from 'react-i18next';
import { formatDate, formatTime } from '@/lib/format';
import {
  byServiceDateDescending,
  serviceDate,
  type Job,
  type Property,
} from '@/routes/customers/useCustomers';

/**
 * Everything ever done for one customer, newest first (M1-04).
 *
 * <b>Across every property they own</b>, which is the point of putting it on
 * the customer rather than the property: "have we been out to these people
 * before?" is not a question about one address. The property is a column here,
 * not a filter.
 *
 * <b>What is missing, and why.</b> The AC also asks for an amount per job and
 * an outstanding balance. There is no money in the data model yet — a job has
 * no amount, and the invoice entity is M2-11 with the shared line-item model
 * in M2-04. Inventing a number here would mean deciding the pricing model in a
 * table cell, so the column is absent rather than empty or fabricated, and
 * those two AC lines are carried to M2.
 */
/**
 * The heading `CustomerDetail` renders above this table, and the region's
 * accessible name (M1-42). Exported rather than written out twice: the name
 * has to stay identical to the visible heading, and a literal in each file is
 * exactly how that stops being true.
 */
export const SERVICE_HISTORY_HEADING_ID = 'customer-service-history-heading';

export function ServiceHistory({
  jobs,
  properties,
  timeZone,
}: {
  jobs: readonly Job[];
  /** For naming the address a job was at; a job whose property is archived still shows it. */
  properties: readonly Property[];
  /** The business's clock. A job's date is the date it happened THERE. */
  timeZone: string | undefined;
}) {
  const { t } = useTranslation();
  const ordered = byServiceDateDescending(jobs);
  const addressOf = new Map(
    properties.map((property) => [
      property.id,
      property.unit ? `${property.address.line1}, ${property.unit}` : property.address.line1,
    ]),
  );

  return (
    <div
      role="region"
      aria-labelledby={SERVICE_HISTORY_HEADING_ID}
      className="overflow-x-auto rounded-lg border"
      // WCAG 2.1.1 (M1-42), and this is the clearest failure of the six.
      //
      // ⚠️ Measured in Chrome at the 320px reflow width WCAG 1.4.10 asks for:
      // 320px of content in a 287px box, and `scrollLeft` runs out at 33.3px —
      // the subpixel figure, because the integer `scrollWidth - clientWidth`
      // reads a point higher. No overflow at 640, 700 or 768 — Property drops out
      // below `md` and Technician below `sm`, which is enough room at those
      // widths but not at 320.
      //
      // ⚠️ And there are ZERO focusable descendants: every cell is text. So
      // unlike the other tables there is no incidental route in — no link to
      // tab to that would drag the container sideways. Before this attribute
      // those 33px were reachable by pointer and by nothing else.
      //
      // Named from the visible <h2> above it rather than a string of its own,
      // so the region cannot drift from the heading a sighted user reads.
      // `<div role="region">` rather than a `<section>` for the lint reason
      // recorded in eslint.config.js; the computed role is the same. The table
      // keeps its own <caption>: that describes the contents, this names the
      // thing you land on.
      tabIndex={0}
    >
      <table className="w-full text-sm">
        <caption className="sr-only">{t('customerDetail.historyCaption')}</caption>
        <thead className="border-b bg-[var(--color-muted)]">
          <tr>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              {t('customerDetail.columnDate')}
            </th>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              {t('customerDetail.columnWork')}
            </th>
            <th scope="col" className="hidden px-4 py-2 text-left font-medium md:table-cell">
              {t('customerDetail.columnProperty')}
            </th>
            <th scope="col" className="hidden px-4 py-2 text-left font-medium sm:table-cell">
              {t('customerDetail.columnTechnician')}
            </th>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              {t('customerDetail.columnStatus')}
            </th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((job) => {
            const when = serviceDate(job);
            return (
              <tr
                key={job.id}
                className="border-b align-top last:border-b-0"
                data-testid="history-row"
              >
                <td className="px-4 py-2 whitespace-nowrap">
                  {when ? (
                    <>
                      {/* The DATE is read on the business's clock too, not
                          just the time beside it. `formatDate` defaults to the
                          viewer's zone, and pairing that with a
                          business-zone time puts two different days on one
                          row: 2026-08-02T01:30Z is "Aug 1, 8:30 PM" in
                          Chicago and "Aug 2" to a viewer in Tokyo. Found in
                          the browser — jsdom cannot see it, and neither can a
                          developer sitting in the account's own zone. */}
                      <time dateTime={when} className="block">
                        {formatDate(when, { dateStyle: 'medium', timeZone })}
                      </time>
                      <span className="block text-xs text-[var(--color-muted-foreground)]">
                        {formatTime(when, timeZone)}
                      </span>
                    </>
                  ) : (
                    // Not a blank cell: a job with no date is waiting to be
                    // scheduled, which is a fact worth reading, and a blank
                    // reads as missing data.
                    <span className="text-[var(--color-muted-foreground)]">
                      {t('customerDetail.notScheduled')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <span className="block">{job.jobType ?? t('customerDetail.noJobType')}</span>
                  <span className="block text-xs text-[var(--color-muted-foreground)]">
                    {job.description}
                  </span>
                  {/* Hidden columns stay reachable on a phone rather than
                      merely invisible. */}
                  <span className="block text-xs text-[var(--color-muted-foreground)] md:hidden">
                    {addressOf.get(job.propertyId) ?? job.propertyLine1}
                  </span>
                  <span className="block text-xs text-[var(--color-muted-foreground)] sm:hidden">
                    {job.assignedUserName ?? t('customerDetail.unassigned')}
                  </span>
                </td>
                <td className="hidden px-4 py-2 text-[var(--color-muted-foreground)] md:table-cell">
                  {addressOf.get(job.propertyId) ?? job.propertyLine1}
                </td>
                <td className="hidden px-4 py-2 sm:table-cell">
                  {job.assignedUserName ?? (
                    <span className="text-[var(--color-muted-foreground)]">
                      {t('customerDetail.unassigned')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {/* A word in a chip, never a colour on its own — the same
                      rule the board follows, and the same shared key. */}
                  <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                    {t(`common.jobStatus.${job.status}`, { defaultValue: job.status })}
                  </span>
                  {job.priority === 'Emergency' || job.priority === 'High' ? (
                    <span className="mt-1 block w-fit rounded-full border border-[var(--color-destructive)] px-2 py-0.5 text-xs font-medium text-[var(--color-destructive)]">
                      {t(`common.jobPriority.${job.priority}`, { defaultValue: job.priority })}
                    </span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
